import { Editor, HistoryEditor, Range, Text, Transforms } from '../../core';
import type { DictationCheckItem } from './types';
import { getDictationCheckStore } from './store';
import { AUTO_APPLY_AUTOCORRECT } from '../../../ReportCorrections/constants';

/** Includes normalization in the same undo step and exempts these edits from user-edit handling. */
function batch(editor: Editor, action: () => void): void {
  getDictationCheckStore(editor).withAction(() => {
    const run = () => Editor.withoutNormalizing(editor, action);
    if (HistoryEditor.isHistoryEditor(editor)) HistoryEditor.withNewBatch(editor, run);
    else run();
    getDictationCheckStore(editor).recordHistory();
  });
}

function replaceText(
  editor: Editor,
  item: DictationCheckItem,
  text: string,
  restoreOriginal = false
): boolean {
  const range = item.rangeRef.current;
  if (!range) return false;
  const startRef = Editor.pointRef(editor, Range.start(range), { affinity: 'backward' });
  try {
    if (!Range.isCollapsed(range)) Transforms.delete(editor, { at: range });
    if (!startRef.current) return false;
    if (text) {
      const leaves = (restoreOriginal ? item.originalLeaves : [{ ...item.formatting, text }])
        .filter((leaf) => leaf.text.length > 0)
        .map((leaf) => ({ ...leaf }));
      Transforms.insertNodes(editor, leaves, { at: startRef.current });
      // Reference the inserted leaves themselves. A point at the next leaf's start would
      // absorb a subsequent adjacent insertNodes before normalization runs.
      const entries = [
        ...Editor.nodes(editor, {
          at: [],
          match: (node) => Text.isText(node) && leaves.includes(node),
        }),
      ];
      const first = entries[0];
      const last = entries[entries.length - 1];
      if (!first || !last) return false;
      item.rangeRef.unref();
      item.rangeRef = Editor.rangeRef(
        editor,
        {
          anchor: { path: first[1], offset: 0 },
          focus: { path: last[1], offset: leaves[leaves.length - 1].text.length },
        },
        { affinity: 'inward' }
      );
    } else {
      item.rangeRef.unref();
      item.rangeRef = Editor.rangeRef(
        editor,
        { anchor: startRef.current, focus: startRef.current },
        { affinity: 'inward' }
      );
    }
    return true;
  } finally {
    startRef.unref();
  }
}

function applyItem(editor: Editor, item: DictationCheckItem, auto = false): void {
  const range = item.rangeRef.current;
  if (!range || Editor.string(editor, range) !== item.change.original) return;
  if (!replaceText(editor, item, item.change.replacement)) return;
  const store = getDictationCheckStore(editor);
  if (auto) {
    item.wasAutoApplied = true;
    item.status = 'applied';
    store.update();
  } else {
    store.resolve(item.change.id, 'accepted');
  }
}

export function autoApply(editor: Editor, enabled = AUTO_APPLY_AUTOCORRECT): void {
  if (!enabled) return;
  const items = getDictationCheckStore(editor)
    .getSnapshot()
    .items.filter((item) => item.status === 'open' && item.change.issueType === 'autocorrect');
  if (items.length) batch(editor, () => items.forEach((item) => applyItem(editor, item, true)));
}

export function accept(editor: Editor, id: string): void {
  const item = getDictationCheckStore(editor)
    .getSnapshot()
    .items.find((candidate) => candidate.change.id === id && candidate.status === 'open');
  if (item) batch(editor, () => applyItem(editor, item));
}

export function acceptAll(editor: Editor): void {
  const items = getDictationCheckStore(editor)
    .getSnapshot()
    .items.filter((item) => item.status === 'open');
  if (items.length) batch(editor, () => items.forEach((item) => applyItem(editor, item)));
}

export function dismiss(editor: Editor, id: string): void {
  const store = getDictationCheckStore(editor);
  if (store.getSnapshot().items.some((item) => item.change.id === id && item.status === 'open')) {
    store.resolve(id, 'dismissed');
  }
}

export function dismissAll(editor: Editor): void {
  getDictationCheckStore(editor)
    .getSnapshot()
    .items.forEach((item) => dismiss(editor, item.change.id));
}

export function revert(editor: Editor, id: string): void {
  const store = getDictationCheckStore(editor);
  const item = store.getSnapshot().items.find((candidate) => candidate.change.id === id);
  const range = item?.rangeRef.current;
  if (
    !item ||
    item.status !== 'applied' ||
    !range ||
    Editor.string(editor, range) !== item.change.replacement
  )
    return;
  batch(editor, () => {
    if (replaceText(editor, item, item.replacedText, true)) store.resolve(id, 'reverted');
  });
}

export function revertAllApplied(editor: Editor): void {
  const items = getDictationCheckStore(editor)
    .getSnapshot()
    .items.filter((item) => item.status === 'applied');
  if (!items.length) return;
  const store = getDictationCheckStore(editor);
  batch(editor, () => {
    for (const item of items) {
      const range = item.rangeRef.current;
      if (
        range &&
        Editor.string(editor, range) === item.change.replacement &&
        replaceText(editor, item, item.replacedText, true)
      )
        store.resolve(item.change.id, 'reverted');
    }
  });
}

export function undoResolve(editor: Editor, id: string): void {
  const store = getDictationCheckStore(editor);
  const item = store.getSnapshot().resolved.find((candidate) => candidate.change.id === id);
  const range = item?.rangeRef.current;
  if (!item || !range) return;
  batch(editor, () => {
    // Reopen as a suggestion: never immediately re-apply a correction the user has undone.
    if (
      item.status === 'edited' ||
      item.status === 'accepted' ||
      Editor.string(editor, range) !== item.replacedText
    ) {
      if (!replaceText(editor, item, item.replacedText, true)) return;
    }
    store.reopen(id);
  });
}

/** Called once after the entire history batch, so intermediate delete/insert operations cannot resolve marks. */
export function reconcileHistory(editor: Editor, direction: 'undo' | 'redo'): void {
  const store = getDictationCheckStore(editor);
  const { items, resolved } = store.getSnapshot();
  for (const item of [...items, ...resolved]) {
    const range = item.rangeRef.current;
    if (!range) continue;
    const text = Editor.string(editor, range);
    if (text === item.change.original) {
      if (item.status === 'applied') {
        if (direction === 'redo' && item.lastResolvedStatus === 'reverted')
          store.resolve(item.change.id, 'reverted');
        else item.status = 'open';
      } else if (item.status === 'accepted' || item.status === 'edited')
        store.reopen(item.change.id);
    } else if (
      text === item.change.replacement &&
      (item.status === 'open' || item.status === 'reverted')
    ) {
      if (item.wasAutoApplied && item.lastResolvedStatus !== 'accepted') {
        if (item.status === 'reverted') store.reopen(item.change.id);
        item.status = 'applied';
      } else store.resolve(item.change.id, 'accepted');
    } else if (
      item.status === 'open' &&
      item.lastResolvedStatus === 'edited' &&
      text === item.resolvedText
    ) {
      store.resolve(item.change.id, 'edited');
    }
  }
  store.update();
}
